import EditAutomaticContainersModal from '@/features/pos/components/Cart/parts/EditAutomaticContainersModal'
import { useEditAutomaticContainersModalPortal } from '@/features/pos/components/Cart/parts/EditAutomaticContainersModal/contexts/Portal'
import { CartProductItem } from '@/features/pos/components/Cart/parts/Overview/parts/CartItem/types'
import { useFormAutomaticContainerRemovalsField } from '@/features/pos/components/OrderFinalization/form'
import { useCallback, useEffect, useMemo, useState } from 'react'

interface UseEditAutomaticContainersModalOptions {
    items: CartProductItem[]
    enabled: boolean
    loaded: boolean
}

type ShowFunction = (itemId: string) => void

export const useEditAutomaticContainersModal = ({
    items,
    enabled,
    loaded,
}: UseEditAutomaticContainersModalOptions): ShowFunction => {
    const [editingItemId, setEditingItemId] = useState<string | null>(null)
    const [automaticContainerRemovals, setAutomaticContainerRemovals] =
        useFormAutomaticContainerRemovalsField()

    const editingItem = useMemo(
        () => (enabled && loaded ? items.find(({ id }) => id === editingItemId) : undefined),
        [editingItemId, enabled, items, loaded]
    )

    const hide = useCallback(() => setEditingItemId(null), [])

    const show = useCallback<ShowFunction>(
        (itemId) => {
            if (!enabled || !loaded) return

            setEditingItemId(itemId)
        },
        [enabled, loaded]
    )

    useEffect(() => {
        if (!editingItemId || editingItem) return

        hide()
    }, [editingItem, editingItemId, hide])

    const modal = useMemo(
        () =>
            editingItem ? (
                <EditAutomaticContainersModal
                    key={editingItem.id}
                    item={editingItem}
                    automaticContainerRemovals={automaticContainerRemovals}
                    onAutomaticContainerRemovalsChange={setAutomaticContainerRemovals}
                    onClose={hide}
                />
            ) : null,
        [automaticContainerRemovals, editingItem, hide, setAutomaticContainerRemovals]
    )

    useEditAutomaticContainersModalPortal(!!editingItem, modal)

    return show
}
